// Presentation for the markup an arm builds ITSELF — panels, rows, heroes, rank
// markers — and the formatting that fills them. The complement to surface.ts:
// nothing here names a page class, so a profile template change cannot break
// this file and cannot be fixed in it either. A helper that needs page
// knowledge is a surface.ts recipe, not an armKit helper.
//
// The three things it does take from surface.ts are the ones two sheets must
// not disagree about: the breakpoint tokens, the palette, and the rule-text
// primitive. A panel that stacked at a different width from the page's own
// columns, or a row grey that missed a chip grey, would be two systems.
//
// Every CSS emitter takes the arm's scope first and emits nothing outside it.
// Descendant form only: everything armKit builds lands in a slot or a card, both
// of which sit under the box the harness stamps, so the `${scope}sel` self form
// surface.ts needs for per-card scoping has no use here.
//
// Node builders return DETACHED DOM and never query the document, fetch, or know
// a URL — an arm passes hrefs in, from destinations.ts. Card surgery (chips,
// actions, collapse) lives on ProfileCard, in surface.ts.
//
// The class names below are private on purpose: an arm reaches every one of them
// through a recipe or through kitPartCss, so renaming one is a change to this
// file alone.

import {
  cssRule,
  EDGE,
  FAINT,
  HAIRLINE,
  INK,
  MUTED,
  mediaCss,
} from './surface';

import type { ProfileGaps } from './profileFacts';

const PANEL = 'profile-liautotest-panel';
const PANEL_TITLE = `${PANEL}__title`;
const PANEL_SUBTITLE = `${PANEL}__subtitle`;

const ROWS = 'profile-liautotest-rows';
const ROW_RANK = `${ROWS}__rank`;
const ROW_TITLE = `${ROWS}__title`;
const ROW_META = `${ROWS}__meta`;

const TOTALS = 'profile-liautotest-totals';
const TOTAL = `${TOTALS}__value`;
const TOTAL_NOTE = `${TOTALS}__note`;

const HEADING = 'profile-liautotest-heading';
const MARKER = 'profile-liautotest-marker';
const SUMMARY = 'profile-liautotest-summary';
const WARNING = 'profile-liautotest-warning';
// Not 'profile-liautotest-action': surface.ts already owns that one, on the
// anchors ProfileCard#addAction puts in a card's own actions row.
const CTA = 'profile-liautotest-cta';

const HERO = 'profile-liautotest-hero';
const HERO_TITLE = `${HERO}__title`;
const HERO_META = `${HERO}__meta`;
const HERO_ABSTRACT = `${HERO}__abstract`;
const HERO_ACTIONS = `${HERO}__actions`;
const HERO_ACTION = `${HERO}__action`;

// One attribute for every arm that sections a panel, so no arm invents a
// spelling of its own and groupsCss can separate them without being told which.
const GROUP = 'data-liautotest-group';

// A rough page-to-minutes constant, not a measurement: it exists so two arms
// quoting a reading time quote the same one.
const MINUTES_PER_PAGE = 0.85;

export type Row = {
  rank?: number;
  title: string;
  url: string | null;
  meta?: string;
  onClick?: () => void;
};

export type HeroAction = {
  label: string;
  href: string;
  onClick?: () => void;
};

export type HeroContent = {
  title: string;
  href: string;
  onTitleClick?: () => void;
  meta?: string;
  abstract?: string;
  actions?: HeroAction[];
};

// surface.ts's cssRule takes a selector LIST because a card recipe emits the
// descendant and self forms together; nothing here is ever the scope element
// itself, so this narrows it to the one-selector case.
function ruleFor(
  scope: string,
  selector: string,
  declarations: string,
): string {
  return cssRule([`${scope} ${selector}`], declarations);
}

function anchor(
  className: string,
  text: string,
  href: string,
  onClick?: () => void,
): HTMLAnchorElement {
  const link = document.createElement('a');
  link.className = className;
  link.href = href;
  link.textContent = text;
  if (onClick != null) link.addEventListener('click', onClick);

  return link;
}

function paragraph(className: string, text: string): HTMLParagraphElement {
  const element = document.createElement('p');
  element.className = className;
  element.textContent = text;

  return element;
}

// ---------------------------------------------------------------------------
// Node builders.

export function panel(options: { title?: string; subtitle?: string } = {}): {
  root: HTMLElement;
  body: HTMLElement;
} {
  const root = document.createElement('div');
  root.className = PANEL;

  if (options.title != null) {
    root.appendChild(paragraph(PANEL_TITLE, options.title));
  }
  if (options.subtitle != null) {
    root.appendChild(paragraph(PANEL_SUBTITLE, options.subtitle));
  }

  // Always present, so an arm that fills the panel later has one insertion
  // point whether or not it asked for a title.
  const body = document.createElement('div');
  root.appendChild(body);

  return { root, body };
}

export function rowList(rows: Row[]): HTMLElement {
  const list = document.createElement('ul');
  list.className = ROWS;

  rows.forEach((row) => {
    const item = document.createElement('li');

    if (row.rank != null) {
      const rank = document.createElement('span');
      rank.className = ROW_RANK;
      rank.textContent = String(row.rank);
      item.appendChild(rank);
    }

    // A row with no url is a label, not a dead link: readerGeography lists
    // countries this way.
    const title = document.createElement(row.url == null ? 'span' : 'a');
    title.className = ROW_TITLE;
    title.textContent = row.title;
    if (row.url != null) (title as HTMLAnchorElement).href = row.url;
    if (row.onClick != null) title.addEventListener('click', row.onClick);
    item.appendChild(title);

    if (row.meta != null) {
      const meta = document.createElement('span');
      meta.className = ROW_META;
      meta.textContent = row.meta;
      item.appendChild(meta);
    }

    list.appendChild(item);
  });

  return list;
}

// The uppercase eyebrow above a section of a panel. `span` is for the one that
// sits inside an anchor, where a <p> would be legal but reads wrong.
export function microHeading(
  text: string,
  as: 'p' | 'span' = 'p',
): HTMLElement {
  const element = document.createElement(as);
  element.className = HEADING;
  element.textContent = text;

  return element;
}

// Big figures in a row, with an optional line under them saying what they cover.
// Nulls are dropped and an all-null set yields an EMPTY fragment rather than an
// empty row: a signal with nothing to say must leave no markup, and the note's
// top margin assumes a row above it.
export function totalsRow(
  totals: Array<string | null | undefined>,
  note?: string,
): DocumentFragment {
  const fragment = document.createDocumentFragment();
  const present = labelList(totals);
  if (present.length === 0) return fragment;

  const row = document.createElement('div');
  row.className = TOTALS;
  present.forEach((total) => {
    const value = document.createElement('span');
    value.className = TOTAL;
    value.textContent = total;
    row.appendChild(value);
  });
  fragment.appendChild(row);

  if (note != null) fragment.appendChild(paragraph(TOTAL_NOTE, note));

  return fragment;
}

// A section of a panel. groupsCss separates consecutive ones, so an arm can
// append groups in whatever order its signals arrive and never has to know
// which of them ended up first.
export function group(kind: string): HTMLElement {
  const element = document.createElement('div');
  element.setAttribute(GROUP, kind);

  return element;
}

// A summary button over content that starts hidden. type="button" is load
// bearing — the default is submit, and this can land inside the page's forms.
// onFirstOpen fires on the first reveal only: a reader toggling a list shut and
// open again is one engagement, and counting it twice inflates the metric the
// arm is judged on.
export function disclosure(
  summaryText: string,
  content: HTMLElement,
  onFirstOpen?: () => void,
): DocumentFragment {
  content.hidden = true;

  const summary = document.createElement('button');
  summary.type = 'button';
  summary.className = SUMMARY;
  summary.setAttribute('aria-expanded', 'false');
  summary.textContent = summaryText;

  let opened = false;
  summary.addEventListener('click', () => {
    content.hidden = !content.hidden;
    summary.setAttribute('aria-expanded', String(!content.hidden));

    if (content.hidden || opened) return;
    opened = true;
    onFirstOpen?.();
  });

  const section = document.createDocumentFragment();
  section.appendChild(summary);
  section.appendChild(content);

  return section;
}

// The one prominent link at the foot of a panel — "Add a paper", "Search X",
// "See all reader locations". The href is finished; armKit builds no URLs.
export function panelAction(
  label: string,
  href: string,
  onClick?: () => void,
): HTMLAnchorElement {
  return anchor(CTA, label, href, onClick);
}

// The amber "No file" chip. Detached: the arm appends it to card.chipsRow(),
// because a card that has no item container to hang chips off gets no chip at
// all rather than a stray node.
export function warningChip(
  text: string,
  href: string,
  onClick?: () => void,
): HTMLAnchorElement {
  return anchor(WARNING, text, href, onClick);
}

// Chips can only be appended, so a warning added before a later count lands
// ends up in the middle of the row. Both arms that do this re-append it after
// the counts, and this is how they find it again.
export function warningChipIn(root: ParentNode): HTMLElement | null {
  return root.querySelector<HTMLElement>(`.${WARNING}`);
}

// The rank that goes at the head of a card title, via card.prependToTitle. Takes
// finished text rather than a number: one arm numbers "1." and the rest "1".
export function rankMarker(text: string): HTMLElement {
  const node = document.createElement('span');
  node.className = MARKER;
  node.textContent = text;

  return node;
}

// Fills a panel body with a lead-paper hero. The returned setMeta attaches the
// figures line only once it has something to say and always directly under the
// title, which is what lets an arm render the hero immediately and paint counts
// in when a slow signal lands — a hero waiting on that is worse than control.
export function hero(
  body: HTMLElement,
  content: HeroContent,
): { setMeta: (text: string) => void } {
  const title = anchor(
    HERO_TITLE,
    content.title,
    content.href,
    content.onTitleClick,
  );
  body.appendChild(title);

  let meta: HTMLParagraphElement | null = null;
  const setMeta = (text: string): void => {
    if (text === '') return;

    meta ??= paragraph(HERO_META, '');
    meta.textContent = text;
    if (meta.parentNode == null) body.insertBefore(meta, title.nextSibling);
  };

  if (content.meta != null) setMeta(content.meta);

  if (content.abstract != null && content.abstract !== '') {
    body.appendChild(paragraph(HERO_ABSTRACT, content.abstract));
  }

  const actions = content.actions ?? [];
  if (actions.length > 0) {
    const row = document.createElement('div');
    row.className = HERO_ACTIONS;
    actions.forEach((action) => {
      row.appendChild(
        anchor(HERO_ACTION, action.label, action.href, action.onClick),
      );
    });
    body.appendChild(row);
  }

  return { setMeta };
}

// ---------------------------------------------------------------------------
// CSS. Each emitter is the arm's whole stylesheet for the shape it names; where
// two arms disagreed on a value it is a knob, and where only one arm has an
// opinion it belongs in a kitPartCss call the arm makes for itself.

export type PanelFrame = 'card' | 'flush';

export type PanelCssOptions = {
  // 'card' is the bordered box; 'flush' is the hairline-under-the-block variant
  // every arm that mounts a panel ABOVE the works list uses, so the panel reads
  // as the first band of the column rather than as a floating card.
  frame?: PanelFrame;
  padding?: string;
  marginBottom?: string;
};

export function panelCss(scope: string, options: PanelCssOptions = {}): string {
  const { frame = 'card', marginBottom = '24px' } = options;
  const flush = frame === 'flush';
  const padding = options.padding ?? (flush ? '0 0 20px' : '16px 20px');
  const border = flush
    ? `border: 0; border-bottom: 1px solid ${EDGE}; border-radius: 0;`
    : `border: 1px solid ${EDGE}; border-radius: 6px;`;

  return [
    ruleFor(
      scope,
      `.${PANEL}`,
      `${border} padding: ${padding}; margin-bottom: ${marginBottom};`,
    ),
    ruleFor(
      scope,
      `.${PANEL_TITLE}`,
      `font-size: 15px; font-weight: 700; color: ${INK}; margin: 0 0 2px;`,
    ),
    ruleFor(
      scope,
      `.${PANEL_SUBTITLE}`,
      `font-size: 13px; color: ${MUTED}; margin: 0 0 12px;`,
    ),
  ].join('');
}

export type RowsCssOptions = {
  marginTop?: string;
  // The vertical padding of one row. Tighter than the default wherever a panel
  // carries several lists rather than one.
  itemPadding?: string;
  // The meta column is a nowrap figure at the end of a flex row, so on a phone
  // it squeezes the title to nothing. Hidden there by default; the one arm whose
  // meta IS the point keeps it.
  metaOnPhone?: boolean;
};

export function rowsCss(scope: string, options: RowsCssOptions = {}): string {
  const { marginTop, itemPadding = '6px 0', metaOnPhone = false } = options;

  return [
    ruleFor(
      scope,
      `.${ROWS}`,
      `list-style: none; padding: 0; ` +
        `margin: ${marginTop == null ? '0' : `${marginTop} 0 0`};`,
    ),
    ruleFor(
      scope,
      `.${ROWS} li`,
      `display: flex; align-items: baseline; gap: 8px; ` +
        `padding: ${itemPadding}; border-top: 1px solid ${HAIRLINE};`,
    ),
    ruleFor(scope, `.${ROWS} li:first-child`, 'border-top: 0;'),
    ruleFor(
      scope,
      `.${ROW_RANK}`,
      `flex: 0 0 auto; min-width: 18px; font-size: 12px; color: ${FAINT}; ` +
        'font-variant-numeric: tabular-nums;',
    ),
    ruleFor(
      scope,
      `.${ROW_TITLE}`,
      'flex: 1 1 auto; font-size: 14px; line-height: 1.35;',
    ),
    ruleFor(
      scope,
      `.${ROW_META}`,
      `flex: 0 0 auto; font-size: 12px; color: ${MUTED}; white-space: nowrap;`,
    ),
    metaOnPhone
      ? ''
      : mediaCss('phone', ruleFor(scope, `.${ROW_META}`, 'display: none;')),
  ].join('');
}

export function totalsCss(
  scope: string,
  options: { marginTop?: string } = {},
): string {
  const { marginTop = '0' } = options;

  return [
    ruleFor(
      scope,
      `.${TOTALS}`,
      `display: flex; flex-wrap: wrap; gap: 2px 26px; margin-top: ${marginTop};`,
    ),
    ruleFor(
      scope,
      `.${TOTAL}`,
      `font-size: 20px; font-weight: 700; line-height: 1.25; color: ${INK};`,
    ),
    ruleFor(
      scope,
      `.${TOTAL_NOTE}`,
      `font-size: 13px; color: ${MUTED}; margin: 4px 0 0;`,
    ),
  ].join('');
}

// color: null leaves the colour to the box the heading sits in — the one arm
// that puts an eyebrow inside a filled CTA needs it to inherit white.
export function microHeadingCss(
  scope: string,
  options: { margin?: string; color?: string | null } = {},
): string {
  const { margin = '0 0 4px', color = MUTED } = options;

  return ruleFor(
    scope,
    `.${HEADING}`,
    'display: block; font-size: 11px; font-weight: 700; ' +
      `letter-spacing: 0.08em; text-transform: uppercase; margin: ${margin};` +
      (color == null ? '' : ` color: ${color};`),
  );
}

// rule: null separates groups by space alone. With a hairline the gap is spent
// twice, above and below the line, so the two arms read as one console.
export function groupsCss(
  scope: string,
  options: { gap?: string; rule?: string | null } = {},
): string {
  const { gap = '14px', rule = null } = options;
  const declarations =
    rule == null
      ? `margin-top: ${gap};`
      : `margin-top: ${gap}; padding-top: ${gap}; border-top: ${rule};`;

  return ruleFor(scope, `[${GROUP}] + [${GROUP}]`, declarations);
}

// The triangle is content rather than a background image so it inherits the
// button's colour and survives a print stylesheet.
export function disclosureCss(scope: string): string {
  return [
    ruleFor(
      scope,
      `.${SUMMARY}`,
      'appearance: none; background: none; border: 0; padding: 0; ' +
        `font-size: 14px; line-height: 1.4; color: ${MUTED}; ` +
        'text-align: left; cursor: pointer;',
    ),
    ruleFor(scope, `.${SUMMARY}::after`, "content: ' ▸';"),
    ruleFor(
      scope,
      `.${SUMMARY}[aria-expanded='true']::after`,
      "content: ' ▾';",
    ),
  ].join('');
}

export function panelActionCss(
  scope: string,
  options: { fontSize?: string; marginTop?: string } = {},
): string {
  const { fontSize = '13px', marginTop = '12px' } = options;

  return ruleFor(
    scope,
    `.${CTA}`,
    `display: inline-block; margin-top: ${marginTop}; ` +
      `font-size: ${fontSize}; font-weight: 600;`,
  );
}

// Amber rather than red: a paper with no file is a reminder, not an error, and
// the chip has to sit in a row of neutral counts without shouting over them.
// The hover keeps the colour because the surrounding chips are not links.
//
// Both hexes stay literal because they were checked and no token matches: the DS
// carries no amber TEXT colour at all, and its warning fills (--brand-colors-
// info-100 #fee294, --premium-light #f9f6ed) are a saturated yellow and a beige,
// neither of which reads as a reminder behind #8a5a00.
export function warningChipCss(scope: string): string {
  return [
    ruleFor(
      scope,
      `.${WARNING}`,
      'color: #8a5a00; background: #fbf1de; border-radius: 10px; padding: 0 8px;',
    ),
    ruleFor(
      scope,
      `.${WARNING}:hover`,
      'color: #8a5a00; text-decoration: underline;',
    ),
  ].join('');
}

export type RankMarkerCssOptions = {
  // The gutter the numbers align in. Wide enough for the longest rank the arm
  // will issue, or the titles step right partway down the list.
  minWidth?: string;
  // null inherits the title's size, which is what the one arm inside a heading
  // wants.
  fontSize?: string | null;
  marginRight?: string | null;
  // The marker sits inside the card title, so on a phone the gutter competes
  // with the title for the line.
  narrowMinWidth?: string | null;
};

export function rankMarkerCss(
  scope: string,
  options: RankMarkerCssOptions = {},
): string {
  const {
    minWidth = '20px',
    fontSize = '12px',
    marginRight = null,
    narrowMinWidth = null,
  } = options;

  const declarations =
    `display: inline-block; min-width: ${minWidth}; color: ${FAINT}; ` +
    'font-variant-numeric: tabular-nums;' +
    (fontSize == null ? '' : ` font-size: ${fontSize};`) +
    (marginRight == null ? '' : ` margin-right: ${marginRight};`);

  return [
    ruleFor(scope, `.${MARKER}`, declarations),
    narrowMinWidth == null
      ? ''
      : mediaCss(
          'phone',
          ruleFor(scope, `.${MARKER}`, `min-width: ${narrowMinWidth};`),
        ),
  ].join('');
}

export function heroCss(
  scope: string,
  options: { abstractLineHeight?: string; abstractMarginBottom?: string } = {},
): string {
  const { abstractLineHeight = '1.55', abstractMarginBottom = '14px' } =
    options;

  return [
    ruleFor(
      scope,
      `.${HERO_TITLE}`,
      'display: inline-block; font-size: 20px; font-weight: 700; ' +
        `line-height: 1.3; color: ${INK}; margin: 0 0 4px;`,
    ),
    // tabular figures unconditionally: every arm's meta line is counts or page
    // numbers, and a proportional set makes two heroes' lines disagree in width.
    ruleFor(
      scope,
      `.${HERO_META}`,
      `font-size: 13px; color: ${MUTED}; margin: 0 0 10px; ` +
        'font-variant-numeric: tabular-nums;',
    ),
    ruleFor(
      scope,
      `.${HERO_ABSTRACT}`,
      `font-size: 15px; line-height: ${abstractLineHeight}; color: ${INK}; ` +
        `margin: 0 0 ${abstractMarginBottom};`,
    ),
    ruleFor(
      scope,
      `.${HERO_ACTIONS}`,
      'display: flex; flex-wrap: wrap; gap: 8px 20px;',
    ),
    ruleFor(scope, `.${HERO_ACTION}`, 'font-size: 14px; font-weight: 600;'),
  ].join('');
}

// Escape hatch: this file owns WHICH element, the arm owns WHICH declarations.
// Reach for a named recipe first — a rule emitted here carries no invariant, so
// a change to one of these classes can only be chased through the arms that
// named it. Emitted at the same weight as the recipe's own rule, so an override
// has to be injected AFTER the recipe to win.
export type KitPart =
  | 'panel'
  | 'panelTitle'
  | 'panelSubtitle'
  | 'rows'
  | 'row'
  | 'rowRank'
  | 'rowTitle'
  | 'rowMeta'
  | 'totals'
  | 'total'
  | 'heading'
  | 'marker'
  | 'summary'
  | 'action'
  | 'heroTitle'
  | 'heroMeta'
  | 'heroAbstract'
  | 'heroActions'
  | 'heroAction';

const KIT_PART_CSS: Record<KitPart, string> = {
  panel: `.${PANEL}`,
  panelTitle: `.${PANEL_TITLE}`,
  panelSubtitle: `.${PANEL_SUBTITLE}`,
  rows: `.${ROWS}`,
  row: `.${ROWS} li`,
  rowRank: `.${ROW_RANK}`,
  rowTitle: `.${ROW_TITLE}`,
  rowMeta: `.${ROW_META}`,
  totals: `.${TOTALS}`,
  total: `.${TOTAL}`,
  heading: `.${HEADING}`,
  marker: `.${MARKER}`,
  summary: `.${SUMMARY}`,
  action: `.${CTA}`,
  heroTitle: `.${HERO_TITLE}`,
  heroMeta: `.${HERO_META}`,
  heroAbstract: `.${HERO_ABSTRACT}`,
  heroActions: `.${HERO_ACTIONS}`,
  heroAction: `.${HERO_ACTION}`,
};

export function kitPartCss(
  part: KitPart,
  scope: string,
  declarations: string,
): string {
  return ruleFor(scope, KIT_PART_CSS[part], declarations);
}

// ---------------------------------------------------------------------------
// Formatting. Every one of these answers null for "unknown", and null is never
// the same answer as zero: a count nobody could fetch must not render as a
// figure the author reads as fact.

// Non-positive reads as unknown wherever a number is a size or a reach: the
// page-count endpoint spells "no page count" as 0, and "0 readers" argues
// against the click it is printed beside.
export function positive(value: number | null | undefined): number | null {
  return value != null && value > 0 ? value : null;
}

export function pageLabel(pageCount: number | null | undefined): string | null {
  const pages = positive(pageCount);
  if (pages == null) return null;

  return `${pages} ${pages === 1 ? 'page' : 'pages'}`;
}

export function readingTime(
  pageCount: number | null | undefined,
): string | null {
  const pages = positive(pageCount);
  if (pages == null) return null;

  const minutes = Math.round(pages * MINUTES_PER_PAGE);
  if (minutes < 1) return null;
  // Past twenty minutes the estimate is not worth a one-minute resolution, and
  // a rounded figure reads as an estimate rather than as a measurement.
  if (minutes <= 20) return `~${minutes} min`;

  return `~${Math.round(minutes / 5) * 5} min`;
}

// Enough English for the nouns the arms actually count: a consonant before a
// final y takes -ies and a sibilant takes -es. Appending a bare s is what left
// reader_geography carrying its own pluralizer for "countries" and
// "universities". Anything irregular is the caller's to pass explicitly.
function pluralOf(singular: string): string {
  if (/[^aeiou]y$/.test(singular)) return `${singular.slice(0, -1)}ies`;
  if (/(s|x|z|ch|sh)$/.test(singular)) return `${singular}es`;

  return `${singular}s`;
}

// Zero IS rendered here, unlike everywhere else: the caller has already decided
// the figure is worth printing, and readerGeography prints a real zero-hit row.
// Pass positive(value) to get the unknown-and-zero-alike reading.
export function countLabel(
  value: number | null | undefined,
  singular: string,
  plural = pluralOf(singular),
): string | null {
  if (value == null) return null;

  return `${value.toLocaleString()} ${value === 1 ? singular : plural}`;
}

export function labelList(parts: Array<string | null | undefined>): string[] {
  return parts.filter((part): part is string => part != null && part !== '');
}

export function joinLabels(
  parts: Array<string | null | undefined>,
  separator = ' · ',
): string {
  return labelList(parts).join(separator);
}

// A total nobody contributed to is unknown, and a zero total is a line the
// author is better off not reading: both leave the line out.
export function totalOf(counts: Map<number, number>): number | null {
  return positive([...counts.values()].reduce((sum, count) => sum + count, 0));
}

// The first n sentences of a body of text, for a digest that must read as prose
// rather than as a truncation.
export function firstSentences(text: string, count: number): string {
  const trimmed = text.trim();
  if (trimmed === '') return '';

  const sentences = trimmed.match(/[^.!?]+[.!?]+(\s|$)/g);
  if (sentences == null) return trimmed;

  return sentences.slice(0, count).join('').trim();
}

// Substance order: a readable file first, then page count descending, with
// unknown page counts sinking below known ones rather than sorting as zero. A
// zero page count IS unknown — the endpoint spells "we never counted" that way,
// and it is already how pageLabel reads it, so the sort key and the chip cannot
// disagree about which papers have a length.
export function bySubstance(
  pageCountOf: (workId: number) => number | null | undefined,
): (
  a: { workId: number; hasDownloadable: boolean },
  b: { workId: number; hasDownloadable: boolean },
) => number {
  return (a, b) => {
    if (a.hasDownloadable !== b.hasDownloadable) {
      return a.hasDownloadable ? -1 : 1;
    }

    const left = positive(pageCountOf(a.workId));
    const right = positive(pageCountOf(b.workId));
    if (left == null && right == null) return 0;
    if (left == null) return 1;
    if (right == null) return -1;

    return right - left;
  };
}

// Fixed order, not the order the gaps object happens to enumerate in: two arms
// list these and they must read the same on a profile missing all four.
const GAP_LABELS: ReadonlyArray<{ key: keyof ProfileGaps; label: string }> = [
  { key: 'bio', label: 'a biography' },
  { key: 'affiliation', label: 'a department' },
  { key: 'cv', label: 'a CV' },
  { key: 'photo', label: 'a photo' },
];

// True in ProfileGaps means MISSING, so this lists what to ask for. The edit
// url is one url for all four — the profile editor has no per-field entry — and
// comes from destinations.ts, never from here.
export function gapRows(
  gaps: ProfileGaps,
  editUrl: string,
  onPick: (field: keyof ProfileGaps) => void,
): Row[] {
  return GAP_LABELS.filter(({ key }) => gaps[key]).map(({ key, label }) => ({
    title: label,
    url: editUrl,
    onClick: () => onPick(key),
  }));
}
